import {
  getImportResultDetails,
  MISSING_MANIFEST_MESSAGE,
} from "./importContentUtils";

const IMPORT_TYPES = {
  JSON: "JSON",
  QTI: "QTI",
};

const IMPORT_TYPE_OPTIONS = [
  { label: "QTI", value: IMPORT_TYPES.QTI },
  { label: "JSON", value: IMPORT_TYPES.JSON },
];

const PACKAGE_CONTENT_TYPE = "application/zip";

// CCR reports what happened to each object; the preview dialog speaks in terms
// of what will happen, using the same statuses as the QTI import.
const PREVIEW_STATUS_BY_RESULT_STATUS = {
  CREATED: "Valid",
  FAILED: "Invalid",
  NO_CHANGE: "No Change",
  UPDATED: "Valid - Overwrite",
};

const JSON_TYPE_HINT =
  "If this is a JSON data package, set Type to JSON and validate again.";

// A Super Admin who validates a JSON package while Type is still QTI gets a
// pointer to the Type dropdown; other users have no such dropdown, so they see
// the plain message.
const getValidationErrorMessage = ({ canImportJson, error, importType }) => {
  const message = error?.message ?? String(error);
  const isJsonPackageSetAsQti =
    canImportJson &&
    importType === IMPORT_TYPES.QTI &&
    message.includes(MISSING_MANIFEST_MESSAGE);
  return isJsonPackageSetAsQti ? `${message}. ${JSON_TYPE_HINT}` : message;
};

const getImportUploadUrlQuery = `
  query getImportUploadUrl($fileName: String!, $program: ID!) {
    getImportUploadUrl(fileName: $fileName, program: $program) {
      expiresIn
      s3Key
      uploadUrl
    }
  }
`;

const importContentFromZipMutation = `
  mutation importContentFromZip(
    $fileName: String!
    $metadata: ImportMetadataInput
    $override: Boolean
    $program: ID!
    $s3Key: String!
    $validateOnly: Boolean
  ) {
    importContentFromZip(
      fileName: $fileName
      metadata: $metadata
      override: $override
      program: $program
      s3Key: $s3Key
      validateOnly: $validateOnly
    ) {
      id
      itemCount
      passageCount
      importResults {
        contentArea
        contentSpecialist {
          id
          name
        }
        details
        developmentState
        id
        itemWriter {
          id
          name
        }
        name
        status
        type
      }
    }
  }
`;

const getPreviewStatus = (resultStatus) =>
  PREVIEW_STATUS_BY_RESULT_STATUS[resultStatus] ?? resultStatus;

// Only objects that will be created or overwritten use the shared wording;
// failed and unchanged objects keep CCR's explanation of what went wrong.
const PREVIEW_STATUSES_WITH_SHARED_DETAILS = ["Valid", "Valid - Overwrite"];

const getPreviewDetails = ({ details, status }) =>
  PREVIEW_STATUSES_WITH_SHARED_DETAILS.includes(status)
    ? getImportResultDetails(status)
    : (details ?? "");

const toPreviewRow = (importResult) => {
  const status = getPreviewStatus(importResult.status);
  return {
    details: getPreviewDetails({ details: importResult.details, status }),
    name: importResult.name,
    status,
    type: importResult.type,
  };
};

const buildImportMetadata = ({
  contentArea,
  contentSpecialist,
  developmentState,
  itemWriter,
}) => ({
  ...(contentArea && { contentArea }),
  ...(contentSpecialist && { contentSpecialist }),
  ...(developmentState && { developmentState }),
  ...(itemWriter && { itemWriter }),
});

// The server's reason (e.g. "ZIP file is empty") when there is one, without
// urql's "[GraphQL]" prefix; otherwise the generic message for the step.
const getErrorMessage = (fallbackMessage) => (response) =>
  response?.error?.graphQLErrors?.[0]?.message ??
  response?.error?.message ??
  fallbackMessage;

const getImportUploadUrl =
  (client) =>
  async ({ fileName, program }) => {
    const response = await client
      .query(
        getImportUploadUrlQuery,
        { fileName, program },
        { requestPolicy: "network-only" },
      )
      .toPromise();
    const uploadUrl = response?.data?.getImportUploadUrl;
    if (response?.error || !uploadUrl)
      throw new Error(
        getErrorMessage("Unable to prepare the JSON package upload")(response),
      );
    return uploadUrl;
  };

const uploadPackage =
  (file) =>
  async ({ uploadUrl }) => {
    const response = await fetch(uploadUrl, {
      body: file,
      headers: { "Content-Type": PACKAGE_CONTENT_TYPE },
      method: "PUT",
    });
    if (!response.ok)
      throw new Error(`JSON package upload failed (${response.status})`);
  };

const runPackageImport = (client) => async (variables) => {
  const response = await client
    .mutation(importContentFromZipMutation, variables, {
      requestPolicy: "network-only",
    })
    .toPromise();
  const importEvent = response?.data?.importContentFromZip;
  if (response?.error || !importEvent)
    throw new Error(getErrorMessage("JSON package import failed")(response));
  return importEvent;
};

// Uploads the package and asks CCR to classify its content without importing it.
// The uploaded package is kept so the follow-up import can reuse its s3Key.
const validateJsonPackage =
  (client) => (program) => (metadata) => async (file) => {
    const uploadUrl = await getImportUploadUrl(client)({
      fileName: file.name,
      program,
    });
    await uploadPackage(file)(uploadUrl);

    const importEvent = await runPackageImport(client)({
      fileName: file.name,
      metadata: buildImportMetadata(metadata),
      override: true,
      program,
      s3Key: uploadUrl.s3Key,
      validateOnly: true,
    });

    return {
      data: (importEvent.importResults ?? []).map(toPreviewRow),
      s3Key: uploadUrl.s3Key,
    };
  };

const importJsonPackage =
  (client) =>
  (program) =>
  (metadata) =>
  async ({ file, s3Key }) =>
    runPackageImport(client)({
      fileName: file.name,
      metadata: buildImportMetadata(metadata),
      override: true,
      program,
      s3Key,
      validateOnly: false,
    });

export {
  buildImportMetadata,
  getImportUploadUrlQuery,
  getValidationErrorMessage,
  IMPORT_TYPE_OPTIONS,
  IMPORT_TYPES,
  importContentFromZipMutation,
  importJsonPackage,
  toPreviewRow,
  validateJsonPackage,
};
